import { useEffect, useState } from 'react';
import { Notification, NotificationType } from '../contexts/NotificationContext';

interface ToastProps {
  notification: Notification;
  onClose: () => void;
}

const getToastStyles = (type: NotificationType) => {
  const styles = {
    success: {
      container: 'bg-white border-success-600',
      icon: 'bg-success-600',
      title: 'text-success-700',
      message: 'text-text-secondary',
    },
    error: {
      container: 'bg-white border-error-600',
      icon: 'bg-error-600',
      title: 'text-error-700',
      message: 'text-text-secondary',
    },
    warning: {
      container: 'bg-white border-warning-600',
      icon: 'bg-warning-600',
      title: 'text-warning-700',
      message: 'text-text-secondary',
    },
    info: {
      container: 'bg-white border-primary-900',
      icon: 'bg-primary-900',
      title: 'text-text-primary',
      message: 'text-text-secondary',
    },
  };
  return styles[type];
};

const getIcon = (type: NotificationType) => {
  switch (type) {
    case 'success':
      return (
        <svg className="w-4 h-4 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
        </svg>
      );
    case 'error':
      return (
        <svg className="w-4 h-4 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
        </svg>
      );
    case 'warning':
      return (
        <svg className="w-4 h-4 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
        </svg>
      );
    case 'info':
      return (
        <svg className="w-4 h-4 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
      );
  }
};

export const Toast = ({ notification, onClose }: ToastProps) => {
  const [isVisible, setIsVisible] = useState(false);
  const [isLeaving, setIsLeaving] = useState(false);
  const styles = getToastStyles(notification.type);

  useEffect(() => {
    // Анимация появления
    setTimeout(() => setIsVisible(true), 10);
  }, []);

  const handleClose = () => {
    setIsLeaving(true);
    setTimeout(() => {
      onClose();
    }, 300); // Длительность анимации исчезновения
  };

  return (
    <div
      className={`
        border-2 rounded p-3 transition-all duration-300 ease-in-out
        ${styles.container}
        ${isVisible && !isLeaving ? 'translate-x-0 opacity-100' : 'translate-x-full opacity-0'}
        w-full sm:w-96 max-w-full
      `}
    >
      <div className="flex gap-3">
        <div className={`w-5 h-5 ${styles.icon} rounded flex items-center justify-center flex-shrink-0`}>
          {getIcon(notification.type)}
        </div>
        <div className="flex-1 min-w-0">
          <h4 className={`${styles.title} font-medium mb-0.5 text-sm`}>{notification.title}</h4>
          {notification.message && (
            <p className={`${styles.message} text-xs`}>{notification.message}</p>
          )}
        </div>
        <button
          onClick={handleClose}
          className="flex-shrink-0 text-text-tertiary hover:text-text-secondary transition-colors"
          aria-label="Закрыть"
        >
          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </div>
    </div>
  );
};
